import { inject, Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { PlatformService } from '@core/services/platform.service';
import { DOCUMENT, Location } from '@angular/common';
import { Request } from 'express';
import { REQUEST } from '../../../express.tokens';
import { environment } from '@environments/environment';

@Injectable({
    providedIn: 'root'
})
export class UrlHelperService {
    private readonly location = inject(Location);
    private readonly router = inject(Router);
    private readonly document = inject(DOCUMENT);
    private readonly request: Request | null = inject(REQUEST, { optional: true });
    private readonly platformService: PlatformService = inject(PlatformService);

    private currentRootDomain?: string;
    private currentOrigin?: string;

    /**
     * Returns current absolute url as an URL object - needs "url-polyfill-light" for IE compatibility.
     */
    public getCurrentUrl(): URL {
        return this.request
            ? new URL(this.request.url, `${this.request.protocol}://${this.request.hostname}`)
            : new URL(this.document.location.href);
    }

    /**
     * Returns the origin (host including protocol and port).
     */
    public getOrigin(): string {
        if (this.currentOrigin) {
            return this.currentOrigin;
        }
        this.currentOrigin = this.getCurrentUrl().origin;
        return this.currentOrigin;
    }

    /**
     * Gets current root domain i.e. without any sub-domains e.g. "google.com".
     */
    public getRootDomain(): string {
        if (this.currentRootDomain) {
            return this.currentRootDomain;
        }
        const domainNameArray = (this.request ? (this.request.hostname ?? '') : this.document.location.hostname).split(
            '.'
        );
        while (domainNameArray.length > 2) {
            domainNameArray.shift();
        }

        this.currentRootDomain = domainNameArray.join('.');

        // only in case we build for production and run in localhost
        // or we can change the environment.prod with the apiDomain to emulate it
        if (this.currentRootDomain?.includes('localhost')) {
            this.currentRootDomain = environment.apiDomain.length ? environment.apiDomain : 'kkande-ci.com';
        }
        return this.currentRootDomain;
    }

    /**
     * Takes url as string extracts params and returns a set [ path, params ];
     * @param url
     */
    public toUrlSet(url: string): [string, Params | null] {
        const urlSegments = url.split('?');

        if (!urlSegments?.[1] || this.platformService.isServer) {
            return [url, null];
        }

        const path = urlSegments[0];

        try {
            const decodedQuery = decodeURIComponent(urlSegments[1]);
            const searchParams = new URLSearchParams(decodedQuery);
            return [path, Object.fromEntries(searchParams)];
        } catch {
            console.warn('Error tried to decode malformed URI');
            return [path, null];
        }
    }

    public getRelativeUrl(fullUrl: string): URL {
        return new URL(fullUrl);
    }

    /**
     * Use this to update the url state on the address bar without triggering the reload or other changes
     * @param params
     */
    public updateUrlState(params: Params | null) {
        this.location.replaceState(this.constructFilterUrl(params));
    }

    /**
     * At the time only da-dk market is available
     * we therefore default to that market
     */
    public get getCurrentMarket(): string {
        return this.getDefaultMarket;
    }

    public getParamsFromUrl(path: string): Params | null {
        if (!path) {
            return null;
        }

        /*path.split('?');
        const params = a.pathname?.split('/');

        console.log(params);*/

        return null;
    }

    private get getDefaultMarket(): string {
        return 'da-dk';
    }

    private toFilterParams(params: Params | null): Params | null {
        return params && Object.keys(params).length ? params : null;
    }

    private constructFilterUrl(params: Params | null): string {
        return this.router.serializeUrl(this.router.createUrlTree([], { queryParams: this.toFilterParams(params) }));
    }
}
